import React, { useState } from "react";
import {
  MessageSquare,
  Paperclip,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { resolveMediaUrl } from "../../../../lib/utils";
import { getCurrentUserId, getCurrentUserName } from "../../../../utils/UserUtils";

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = String(hours).padStart(2, "0");

  return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
};

const formatSourceLabel = (value = "") => {
  const map = {
    maker_submit: "Maker",
    maker_resubmit: "Maker Resubmission",
    maker_cancel_resubmit: "Maker Resubmission",
    issue_review: "Issue Review",
    issue_reject: "Checker Rejection",
    closure_request: "Closure Request",
    closure_review: "Closure Review",
    closure_reject: "Closure Rejection",
    approve: "Approved",
    reject: "Rejected",
    cancel: "Cancelled",
    issued_cancel: "Cancellation",
    comment: "Comment",
  };

  return map[value] || String(value || "").replaceAll("_", " ");
};

const getSourceBadge = (source) => {
  const src = String(source || "").toLowerCase().trim();
  if (src === "maker_submit")
    return { label: "Maker Submit", color: "bg-blue-100 text-blue-700 border-blue-200" };
  if (src === "maker_resubmit" || src === "maker_cancel_resubmit")
    return { label: "Maker Resubmission", color: "bg-blue-100 text-blue-700 border-blue-200" };
  if (src === "issue_review")
    return { label: "Issue Review", color: "bg-amber-100 text-amber-800 border-amber-200" };
  if (src === "issue_reject" || src === "reject")
    return { label: "Checker Rejection", color: "bg-rose-100 text-rose-700 border-rose-200" };
  if (src === "closure_request")
    return { label: "Closure Request", color: "bg-purple-100 text-purple-700 border-purple-200" };
  if (src === "closure_review")
    return { label: "Closure Review", color: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  if (src === "closure_reject")
    return { label: "Closure Rejection", color: "bg-rose-100 text-rose-700 border-rose-200" };
  if (src === "issued_cancel" || src === "cancel")
    return { label: "Cancellation", color: "bg-amber-100 text-amber-800 border-amber-200" };
  if (src)
    return { label: formatSourceLabel(source), color: "bg-slate-100 text-slate-700 border-slate-200" };
  return null;
};

const resolveDisplayName = (comment) => {
  const currentUserId = getCurrentUserId();
  const currentUserName = getCurrentUserName();
  const rawName = comment?.user_name || comment?.uploaded_by_name || "";

  if (rawName && !rawName.toLowerCase().startsWith("user ")) {
    return rawName;
  }

  const userId = comment?.user_id || comment?.uploaded_by_id;
  if (currentUserId && userId && String(currentUserId) === String(userId) && currentUserName) {
    return currentUserName;
  }

  if (comment?.group_name || comment?.group_code) {
    const grpName = (comment.group_name || comment.group_code).replace(/_/g, " ");
    return `${grpName} User`;
  }

  return rawName || (userId ? `User ${userId}` : "User");
};

export default function PermitQuestionDiscussion({
  logs,
  permit,
  questionId,
}) {
  const [expanded, setExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const effectiveLogs =
    logs && (Array.isArray(logs.comments) || Array.isArray(logs.attachments))
      ? logs
      : permit?.question_logs?.[String(questionId)] ||
        permit?.question_logs?.[Number(questionId)] ||
        {};

  const comments = Array.isArray(effectiveLogs?.comments) ? effectiveLogs.comments : [];
  const attachments = Array.isArray(effectiveLogs?.attachments) ? effectiveLogs.attachments : [];

  const totalEntries = comments.length + attachments.length;
  const displayComments = expanded ? comments : comments.slice(-3);

  const renderDiscussionContent = () => {
    if (totalEntries === 0) {
      return (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs">
          <div className="flex items-center gap-1.5 font-semibold text-slate-700">
            <MessageSquare className="h-3.5 w-3.5 text-orange-500" />
            <span>Discussion & Logs</span>
          </div>
          <p className="mt-1 text-[11px] italic text-slate-400">No discussion yet.</p>
        </div>
      );
    }

    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-xs">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5 font-semibold text-slate-700">
            <MessageSquare className="h-3.5 w-3.5 text-orange-500" />
            <span>Discussion & Logs</span>
            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">
              {totalEntries}
            </span>
          </div>

          {comments.length > 3 && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-[11px] font-medium text-orange-600 hover:text-orange-700"
            >
              {expanded ? (
                <>
                  Show Less <ChevronUp className="h-3 w-3" />
                </>
              ) : (
                <>
                  View All ({comments.length}) <ChevronDown className="h-3 w-3" />
                </>
              )}
            </button>
          )}
        </div>

        <div className="space-y-2">
          {displayComments.map((c) => {
            const badge = getSourceBadge(c.source);
            const src = String(c.source || "").toLowerCase().trim();
            const isRejection = src.includes("reject");
            const isResubmit = src.includes("resubmit");
            const cardBorderBg = isRejection
              ? "border-rose-200 bg-rose-50/40"
              : isResubmit
              ? "border-blue-200 bg-blue-50/30"
              : "border-slate-200/60 bg-white";

            return (
              <div
                key={c.id || `${c.created_at}-${c.comment}`}
                className={`rounded-md border p-2.5 shadow-2xs ${cardBorderBg}`}
              >
                <div className="mb-1 flex flex-wrap items-center justify-between gap-1 text-[11px]">
                  <div className="flex items-center gap-1.5 font-medium text-slate-800">
                    <span className="font-bold text-slate-900">
                      {resolveDisplayName(c)}
                    </span>
                    {c.group_code && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                        {c.group_code}
                      </span>
                    )}
                    {badge && (
                      <span
                        className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${badge.color}`}
                      >
                        {badge.label}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {formatDateTime(c.created_at)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-slate-700">
                  {typeof c.comment === "object" && c.comment !== null
                    ? c.comment.comment || c.comment.value || JSON.stringify(c.comment)
                    : String(c.comment || "")}
                </p>
              </div>
            );
          })}

          {attachments.length > 0 && (
            <div className="mt-2 pt-1">
              <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Log Attachments ({attachments.length})
              </span>
              <div className="flex flex-wrap gap-2">
                {attachments.map((att) => {
                  const url = resolveMediaUrl(att.file_url || att.file);
                  return (
                    <div
                      key={att.id || url}
                      onClick={() => setPreviewImage(url)}
                      className="group relative h-14 w-14 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-2xs cursor-pointer"
                      title={att.uploaded_by_name || "Attachment"}
                    >
                      <img
                        src={url}
                        alt="attachment"
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Paperclip className="h-4 w-4 text-white" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile view: Compact collapsible button */}
      <div className="mt-3 sm:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all touch-manipulation cursor-pointer ${
            mobileOpen
              ? "border border-orange-300 bg-orange-50 text-orange-800 shadow-xs ring-1 ring-orange-200"
              : "border border-slate-200 bg-slate-50/80 text-slate-600 hover:bg-slate-100"
          }`}
          aria-expanded={mobileOpen}
          title={mobileOpen ? "Collapse Discussion & Logs" : "View Discussion & Logs"}
        >
          <MessageSquare
            className={`h-3.5 w-3.5 shrink-0 transition-colors ${
              mobileOpen ? "text-orange-600" : "text-slate-500"
            }`}
          />
          <span>Discussion & Logs</span>
          {totalEntries > 0 && (
            <span className="rounded-full bg-orange-500 px-1.5 py-0.2 text-[10px] font-bold text-white leading-none">
              {totalEntries}
            </span>
          )}
          <ChevronDown
            className={`h-3 w-3 transition-transform duration-200 ${
              mobileOpen ? "rotate-180 text-orange-600" : "text-slate-400"
            }`}
          />
        </button>

        {mobileOpen && (
          <div className="mt-2 animate-fade-in">
            {renderDiscussionContent()}
          </div>
        )}
      </div>

      {/* Desktop / Tablet view: Standard always-visible box */}
      <div className="hidden sm:block mt-3">
        {renderDiscussionContent()}
      </div>

      {/* Attachment Full Preview Modal */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          className="fixed inset-0 z-[280] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
        >
          <div className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-2xl bg-white p-2 shadow-2xl">
            <img
              src={previewImage}
              alt="Preview"
              className="max-h-[85vh] max-w-[85vw] object-contain rounded-xl"
            />
          </div>
        </div>
      )}
    </>
  );
}
